import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle } from 'lucide-react';
import { analytics } from '../services/analytics';

/**
 * AnalyticsPrivacySettings - Component for managing analytics opt-out preferences
 *
 * Allows users to opt in/out of analytics tracking for privacy compliance (GDPR, etc.)
 * Can be added to user profile or settings page.
 *
 * Features:
 * - Opt-out/opt-in toggle
 * - Shows current opt-out status
 * - Explains what data is tracked
 * - Privacy-first approach
 */
function AnalyticsPrivacySettings() {
  const [hasOptedOut, setHasOptedOut] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Check current opt-out status
    try {
      const optedOut = localStorage.getItem('analytics_opt_out') === 'true';
      setHasOptedOut(optedOut);
      setIsEnabled(analytics.isEnabled());
    } catch {
      setHasOptedOut(false);
      setIsEnabled(false);
    }
  }, []);

  const handleToggle = () => {
    if (hasOptedOut) {
      // Opt in
      analytics.optIn();
      setHasOptedOut(false);
      setIsEnabled(true);
    } else {
      // Opt out
      analytics.optOut();
      setHasOptedOut(true);
      setIsEnabled(false);
    }

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <Shield className="w-6 h-6 text-secondary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analytics & Privacy
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            We use Google Analytics to understand how vendors use our waitlist features
            and improve the experience. Your privacy is important to us.
          </p>

          {/* Current Status */}
          <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Analytics Status:
              </span>
              <div className="flex items-center gap-2">
                {isEnabled ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">Enabled</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">Disabled</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* What We Track */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              What we track:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Waitlist join and leave actions</li>
              <li>Slot offer acceptance/decline rates</li>
              <li>Page views and navigation patterns</li>
              <li>Time spent on key features</li>
            </ul>
          </div>

          {/* What We DON'T Track */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              What we DON'T track:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Personal information (names, emails, addresses)</li>
              <li>Payment information</li>
              <li>Private messages or notes</li>
              <li>Exact IP addresses (anonymized)</li>
            </ul>
          </div>

          {/* Toggle Button */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <label htmlFor="analytics-toggle" className="text-sm font-medium text-gray-700">
              {hasOptedOut ? 'Opt in to analytics' : 'Opt out of analytics'}
            </label>
            <button
              id="analytics-toggle"
              onClick={handleToggle}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${hasOptedOut ? 'bg-gray-300' : 'bg-primary'}
              `}
              aria-label={hasOptedOut ? 'Opt in to analytics' : 'Opt out of analytics'}
              aria-checked={!hasOptedOut}
              role="switch"
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${hasOptedOut ? 'translate-x-1' : 'translate-x-6'}
                `}
              />
            </button>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                ✓ Your analytics preferences have been saved
              </p>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-4 p-3 bg-moonbone border border-teal-200 rounded-lg">
            <p className="text-xs text-ink">
              <strong>Note:</strong> Your browser's "Do Not Track" (DNT) setting is automatically
              respected. If DNT is enabled, analytics will be disabled regardless of this setting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsPrivacySettings;
